// =============================================================================
// features/dashboard/components/OriginChannelList.tsx — Como os leads
// conheceram o Banco do Povo.
//
// Responde "quantos estão vindo de rede social?", que o campo `source` não
// responde: lá todo mundo é 'whatsapp', porque é a porta de entrada no sistema,
// não a origem. Este card usa `origin_channel`, coletado pela IA como última
// pergunta da qualificação.
//
// Só conta quem já respondeu. Os ~3.850 leads importados do Chatwoot nunca
// serão perguntados e ficam de fora — incluí-los como "não informado" faria uma
// fatia gigante e inútil enterrar o dado real.
// =============================================================================

import * as React from 'react';

import type { LeadsByOriginChannelItem } from '../../../hooks/dashboard/types';

interface OriginChannelListProps {
  data: LeadsByOriginChannelItem[];
}

const LABELS: Record<LeadsByOriginChannelItem['origin_channel'], string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  indicacao: 'Indicação',
  outro: 'Outro',
};

const COLORS: Record<LeadsByOriginChannelItem['origin_channel'], string> = {
  instagram: 'var(--brand-azul)',
  facebook: 'var(--info)',
  indicacao: 'var(--brand-verde)',
  outro: 'var(--text-3)',
};

export function OriginChannelList({ data }: OriginChannelListProps): React.JSX.Element {
  const sorted = React.useMemo(() => [...data].sort((a, b) => b.count - a.count), [data]);
  const total = sorted.reduce((s, d) => s + d.count, 0);

  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <p
        className="font-sans font-semibold uppercase mb-4"
        style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: 'var(--text-3)' }}
      >
        Como conheceram
        {total > 0 && (
          <span className="ml-1 normal-case font-normal" style={{ letterSpacing: 0 }}>
            ({total.toLocaleString('pt-BR')} respondidos)
          </span>
        )}
      </p>

      {total === 0 ? (
        <div
          className="flex items-center justify-center py-8 text-center"
          style={{ color: 'var(--text-3)' }}
        >
          <p className="font-sans text-sm">
            Ainda sem respostas. A IA pergunta ao final da qualificação.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3" role="list" aria-label="Origem dos leads">
          {sorted.map((item) => {
            const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
            const color = COLORS[item.origin_channel];
            return (
              <li key={item.origin_channel} className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-xs gap-2">
                  <span className="font-sans font-medium" style={{ color: 'var(--text-2)' }}>
                    {LABELS[item.origin_channel]}
                  </span>
                  <span
                    className="font-mono shrink-0 tabular-nums"
                    style={{
                      fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                      color: 'var(--text-3)',
                    }}
                  >
                    {item.count.toLocaleString('pt-BR')}
                    <span className="ml-1 opacity-60">({pct}%)</span>
                  </span>
                </div>
                <div
                  className="h-1.5 rounded-pill overflow-hidden"
                  style={{ background: 'var(--surface-muted)' }}
                  role="presentation"
                  aria-hidden="true"
                >
                  <div
                    className="h-full rounded-pill transition-all duration-slow"
                    style={{ width: `${pct}%`, background: color, minWidth: pct > 0 ? '3px' : '0' }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
